import {logger, Time} from 'yougile-util'
import {DataObjectId, Hub, HubLike, User} from 'yougile-model'
import {SyncCommitHandler} from '@client/sync-commit-handler'
import {Sync} from '../client/sync'
import {Board} from './board'
import {Column} from './column'
import {globals} from './globals-base'
import {SystemEntities} from './system-entities'

/**
 * If object is older then newTimeout it is not considered as new for any user.
 */
const newTimeout = 3 * Time.day

/**
 * Allow to store information about users who've seen the object (usually tasks)
 * already. This information is stored as a modifiers of a connection between
 * the object and #seen-by tag (modifier values are user ids).
 */
export class SeenBy {
  public static isSeen(entity: HubLike, userId: DataObjectId): boolean {
    if (!entity) {
      return false
    }
    const box = Sync.get(entity)
    const seenByTag = SystemEntities.get(entity.companyId).seenByTagId
    return (
      box.isConnected(seenByTag, entity.id) &&
      box.getModifiers(seenByTag, entity.id).includes(userId)
    )
  }

  private static canBeNew(entity: Hub, userId: DataObjectId): boolean {
    const box = Sync.get(entity)
    const user = box.get(userId) as User
    return (
      user &&
      entity.timestamp > Time.now() - newTimeout &&
      !entity.deleted &&
      !entity.title.startsWith('.') &&
      entity.timestamp > user.timestamp
    )
  }

  public static isNew(entity: Hub, userId: DataObjectId): boolean {
    if (globals.sharedId) {
      return false
    }
    return this.canBeNew(entity, userId) && !SeenBy.isSeen(entity, userId)
  }

  public static markSeen(task: Hub, userId: DataObjectId): void {
    if (!task) {
      return
    }
    if (globals.silentMode) {
      return
    }
    const box = Sync.get(task)
    const seenByTag = SystemEntities.get(task.companyId).seenByTagId
    if (!this.canBeNew(task, userId)) {
      return
    }
    if (SeenBy.isSeen(task, userId)) {
      logger.warn('The entity is already seen')
      return
    }
    if (!box.isConnected(seenByTag, task.id)) {
      box.connect(seenByTag, task.id)
    }
    box.addModifiers(seenByTag, task.id, [userId])
  }

  public static markBoardSeen(board: HubLike): void {
    SyncCommitHandler.doInBatch(() =>
      Board.listColumns(board).forEach(col =>
        Column.listTasks(col).forEach(task => {
          if (SeenBy.isNew(task, globals.currentUserId)) {
            SeenBy.markSeen(task, globals.currentUserId)
          }
        })
      )
    )
  }
}
